import { hoverSweepOptions } from '../options/hover-sweep.options';

/**
 * Per-element state stored on `hoverSweepDirective.container`.
 *
 * @typedef {object} HoverSweepState
 * @property {HTMLElement} element The host element.
 * @property {HTMLSpanElement} layer The `.animation` layer (injected by us or pre-rendered by the template).
 * @property {boolean} ownsLayer Whether the directive injected the layer (and may therefore remove it on destroy).
 * @property {(event: MouseEvent) => void} onEnter Bound enter handler (for unbinding).
 * @property {(event: MouseEvent) => void} onLeave Bound leave handler (for unbinding).
 * @property {(event: TransitionEvent) => void} onTransitionEnd Bound transitionend handler (for unbinding).
 */

/**
 * Hover Sweep Directive
 *
 * A global direction-aware hover effect. The directive ensures a single `.animation` child
 * exists in the host (re-using a server-rendered one if present, otherwise injecting one)
 * and animates its `translate` based on where the pointer entered (sweep-in) and where it
 * leaves (sweep-out). Targets `(pointer: fine)` devices via mouse events.
 *
 * State machine (classes on the layer):
 *
 *   idle  ─enter─▶  .is-snapping + .{edge}   ─reflow + RAF─▶   .is-hovering   (at rest)
 *                                                                  │
 *   idle  ◀─transitionend─  .{edge} removed  ◀─leave─  .{edge}, .is-hovering removed
 *
 * @example
 * ```html
 *  <button class="hover-sweep">Hover me</button>
 *  <a class="hover-sweep is-subtle">Subtle preset (CSS class)</a>
 * ```
 */
export const hoverSweepDirective = {
  /**
   * Pre-defined options container.
   */
  options: hoverSweepOptions,

  /**
   * Edge names, ordered to match `Math.atan2` quadrant rounding in {@link hoverSweepDirective.nearestEdge}.
   *
   * @type {ReadonlyArray<'right' | 'top' | 'left' | 'bottom'>}
   */
  edges: Object.freeze(['right', 'top', 'left', 'bottom']),

  /**
   * Class-based selectors picked up by the compiler service alongside attribute names.
   *
   * @type {string[]}
   */
  selectors: ['.hover-sweep'],

  /**
   * Per-element state keyed by `element.ambid` (assigned by the compiler).
   *
   * @type {Map<string, HoverSweepState>}
   */
  container: new Map(),

  /**
   * Initialize directive on element.
   *
   * @param {HTMLElement} element The DOM element.
   * @param {object} options Resolved options from all sources.
   */
  init(element, options = {}) {
    // Guard against double-init — the compiler may dispatch this directive twice for a host
    // that matches both the attribute name AND the class selector.
    if (this.container.has(element.ambid)) {
      return;
    }

    // Re-use a server-rendered layer if the template provided one; otherwise inject our own.
    // Either way the directive owns the event lifecycle.
    let layer = element.querySelector(':scope > .animation');
    const ownsLayer = !layer;

    if (!layer) {
      layer = this.createLayer();
      element.prepend(layer);
    }

    const onEnter = (event) => this.onEnter(element, layer, event);
    const onLeave = (event) => this.onLeave(element, layer, event);
    const onTransitionEnd = (event) => this.onTransitionEnd(layer, event);

    element.addEventListener('mouseenter', onEnter);
    element.addEventListener('mouseleave', onLeave);
    layer.addEventListener('transitionend', onTransitionEnd);

    this.container.set(element.ambid, { element, layer, ownsLayer, onEnter, onLeave, onTransitionEnd });
  },

  /**
   * Clean up when element is removed from DOM.
   *
   * @param {HTMLElement} element The DOM element being removed.
   */
  destroy(element) {
    const state = this.container.get(element.ambid);

    if (!state) {
      return;
    }

    element.removeEventListener('mouseenter', state.onEnter);
    element.removeEventListener('mouseleave', state.onLeave);
    state.layer.removeEventListener('transitionend', state.onTransitionEnd);

    if (state.ownsLayer) {
      state.layer.remove();
    } else {
      state.layer.classList.remove('is-hovering', 'is-snapping', ...this.edges);
    }

    this.container.delete(element.ambid);
  },

  /**
   * Build a fresh sweep layer.
   *
   * @protected
   *
   * @returns {HTMLSpanElement}
   */
  createLayer() {
    const layer = document.createElement('span');

    layer.className = 'animation';
    layer.setAttribute('aria-hidden', 'true');

    return layer;
  },

  /**
   * Mouse enter — snap the layer off-screen on the entry edge (CSS disables the transition
   * while `.is-snapping` is set, so the snap is instant), then on the next frame promote
   * the layer to the "at rest" state. The translate transition runs from the entry edge to
   * centre.
   *
   * @protected
   *
   * @param {HTMLElement} element
   * @param {HTMLSpanElement} layer
   * @param {MouseEvent} event
   */
  onEnter(element, layer, event) {
    const edge = this.nearestEdge(element, event);

    // Stage the snap: add the new state classes first (keeps display: block continuous if a
    // leave was mid-flight), then strip any stale state from a previous cycle.
    layer.classList.add('is-snapping', edge);
    layer.classList.remove('is-hovering', ...this.edges.filter((e) => e !== edge));

    // Force the browser to commit the off-screen snap before we release `.is-snapping`;
    // otherwise the two class changes collapse into a single frame and the sweep is invisible.
    layer.offsetWidth;

    requestAnimationFrame(() => {
      // Abort if a `mouseleave` fired before this frame ran — it would have cleared
      // `.is-snapping` already, and re-promoting to at-rest would yank the layer back from
      // its in-progress exit.
      if (!layer.classList.contains('is-snapping')) {
        return;
      }

      layer.classList.remove('is-snapping', edge);
      layer.classList.add('is-hovering');
    });
  },

  /**
   * Mouse leave — drop the "at rest" class and animate toward the exit edge. The layer
   * remains visible (display: block via the edge class) for the duration of the exit
   * transition, then the persistent {@link hoverSweepDirective.onTransitionEnd} listener
   * strips the edge class and the layer falls back to display: none.
   *
   * @protected
   *
   * @param {HTMLElement} element
   * @param {HTMLSpanElement} layer
   * @param {MouseEvent} event
   */
  onLeave(element, layer, event) {
    // Sample the exit edge BEFORE we touch display state. `nearestEdge` calls
    // `getBoundingClientRect`, which forces a synchronous reflow — and the style committed
    // by that reflow becomes the transition baseline. If we removed `.is-hovering` first,
    // the layer would compute to `display: none` at that moment and the browser would skip
    // the exit transition entirely.
    const edge = this.nearestEdge(element, event);

    // Apply the exit edge BEFORE dropping `.is-hovering` so `display: block` stays continuous
    // through the swap. Translate target moves from 0 to the exit edge; the transition runs.
    layer.classList.add(edge);
    layer.classList.remove('is-hovering', 'is-snapping', ...this.edges.filter((e) => e !== edge));
  },

  /**
   * Persistent `transitionend` handler — fires for every natural completion of a `translate`
   * transition on the layer. Stateless: reads the current class set instead of capturing a
   * closure over an `edge` value, so it can't be tricked by stale state from a previous
   * cycle.
   *
   * Cleanup logic: if the layer is neither at rest (`.is-hovering`) nor mid-snap
   * (`.is-snapping`), it just finished a leave's exit transition — strip the edge class so
   * the base rule reasserts `display: none`. Entry transition completions are ignored
   * (layer has `.is-hovering`, we want it to stay visible).
   *
   * Crucially we do NOT listen for `transitioncancel` — a fresh listener attached during a
   * rapid re-enter would otherwise catch the cancellation of an in-flight transition and
   * mistakenly clean up the live edge class.
   *
   * @protected
   *
   * @param {HTMLSpanElement} layer
   * @param {TransitionEvent} event
   */
  onTransitionEnd(layer, event) {
    if (event.propertyName !== 'translate' || event.target !== layer) {
      return;
    }

    if (layer.classList.contains('is-hovering') || layer.classList.contains('is-snapping')) {
      return;
    }

    this.edges.forEach((edge) => layer.classList.remove(edge));
  },

  /**
   * Closest-edge detection — projects the pointer offset onto the unit square (normalised by
   * aspect ratio) and quantises the resulting angle to one of four cardinal directions. Works
   * on any rectangle, including thin/wide buttons.
   *
   * @protected
   *
   * @param {HTMLElement} element
   * @param {MouseEvent} event
   *
   * @returns {'top' | 'right' | 'bottom' | 'left'}
   */
  nearestEdge(element, event) {
    const rect = element.getBoundingClientRect();
    const w = rect.width || 1;
    const h = rect.height || 1;

    // Map pointer coordinates relative to the element's center
    // and normalize by aspect ratio to handle non-square elements.
    const x = (event.clientX - rect.left - w / 2) * (w > h ? h / w : 1);

    // Invert the Y-axis to convert from browser screen coordinates (Y-down)
    // to standard mathematical Cartesian coordinates (Y-up).
    const y = (rect.top + h / 2 - event.clientY) * (h > w ? w / h : 1);

    // Math.atan2(y, x) returns an angle between -PI and PI.
    // Adding PI/4 (45 degrees) rotates the boundary sectors so that each 90-degree
    // quadrant cleanly encloses and centers around one of the cardinal edges.
    const angle = Math.atan2(y, x) + Math.PI / 4;

    // Normalize negative angles to positive [0, 2*PI) range
    const positiveAngle = angle >= 0 ? angle : (angle + 2 * Math.PI);

    // Divide the 360-degree circle into 4 equal 90-degree sectors
    // 0: Right (-45° to 45°), 1: Top (45° to 135°), 2: Left (135° to 225°), 3: Bottom (225° to 315°)
    const dir = Math.floor(positiveAngle / (Math.PI / 2)) % 4;

    return this.edges[dir];
  }
};
